import omit from 'lodash/omit';
import {
  SECTION_I,
  SECTION_J_ALL,
  SECTION_J_CASH,
  SECTION_J_EFT,
  SECTION_J_PERIODIC,
  SECTION_J_TRANSFER,
  SECTION_O,
  SECTION_R,
  SECTION_W,
} from '@/constants/sections';
import {DocumentData, DocumentValue, Model, ValueResolver, VisibilitySet} from '@/engine';
import {
  isFieldVisible,
  isReferenceToAccount,
  isReferenceToAccountMeta,
  isReferenceToInvestorProfile,
  isReferenceToSourceAccount,
  isReferenceToSourceAccountMeta,
} from '@/engine/utils/reference';
import {logger} from '@/initializers/logger';
import {
  DocumentAttachmentId,
  FullDocument,
  FullDocumentReadyForSignature,
  InternalDocumentAttachmentId,
} from '@/interfaces/document';
import {ALLOCATION_METHOD_FIELD, ALLOCATION_METHOD_FIELDS} from '@/model/helpers/section-j/allocation-method';
import {Reference} from '@/model/types';
import {
  DOMAIN_VALUE_PREFIX,
  DOMAIN_VALUE_BOOLEAN,
  INVESTMENT_ACCOUNT_DATA_TYPES,
  BENEFICIARIES_ARRAY_TYPES,
  ARRAY_DATA_TYPES,
} from '@/model/types/constants';
import transformByFieldName from '@/utils/document/field-value-transform';

const SECTION_MAPPING = {
  'USER': 'sectionUSER',
  'CONS': 'sectionCONS',
  'INVE': 'sectionINVE',
  'COMP': 'sectionCOMP',
  'PRV_COMP': 'sectionPRV_COMP',
  'A': 'sectionA',
  'AA': 'sectionAA',
  'B': 'sectionB',
  'C': 'sectionC',
  'CC': 'sectionCC',
  'D': 'sectionD',
  'DD': 'sectionDD',
  'E': 'sectionE',
  'EE': 'sectionEE',
  'F': 'sectionF',
  'H': 'sectionH',
  'I': 'sectionI',
  'J_Cash': 'sectionJCash',
  'J_EFT': 'sectionJEFT',
  'J_Periodic': 'sectionJPeriodic',
  'J_Transfer': 'sectionJTransfer',
  'J+': 'sectionJPlus',
  'L': 'sectionL',
  'M': 'sectionM',
  'N': 'sectionN',
  'O': 'sectionO',
  'Y': 'sectionY',
  'P': 'sectionP',
  'Q': 'sectionQ',
  'R': 'sectionR',
  'S': 'sectionS',
  'T': 'sectionT',
  'X': 'sectionX',
  'U': 'sectionU',
  'V': 'sectionV',
  'W': 'sectionW',
} as const;

export const META_FIELDS = [
  'id',
  'status',
  'createdAt',
  'createdBy',
  'updatedAt',
  'completedAt',
  'sentForSignatureAt',
  'sentForSignatureFailedAt',
  'signedAt',
  'submittedAt',
  'submitFailedAt',
  'forms',
  'signature',
];

export const omitMetaFields = (data: FullDocument): Partial<FullDocument> => {
  return omit(data, META_FIELDS);
};

export const serializeDocument = (
  data: DocumentData,
  model: Model,
  visibilitySet: Set<string>,
  valueResolver: ValueResolver,
): FullDocument => {
  const serializedDocument = Object.keys(data.data).reduce((memo, key) => {
    if (isReferenceToInvestorProfile(key)) {
      return {
        ...memo,
        sectionINVE: {
          ...(memo.sectionINVE ?? {}),
          [key]: data.data[key],
        },
      };
    }

    if (isReferenceToAccount(key) || isReferenceToAccountMeta(key)) {
      return {
        ...memo,
        sectionCOMP: {
          ...(memo.sectionCOMP ?? {}),
          [key]: data.data[key],
        },
      };
    }

    if (isReferenceToSourceAccount(key) || isReferenceToSourceAccountMeta(key)) {
      return {
        ...memo,
        sectionPRV_COMP: {
          ...(memo.sectionPRV_COMP ?? {}),
          [key]: data.data[key],
        },
      };
    }

    const accessRecord = model.accessMap.get(key);

    const sectionNotFound =
      !accessRecord || !('sectionIndex' in accessRecord) || accessRecord.sectionIndex === undefined;

    const fieldNotVisible = !isFieldVisible(key, visibilitySet);

    if (sectionNotFound || fieldNotVisible) return memo;

    const sectionId = model.form.sections[accessRecord.sectionIndex].id as keyof typeof SECTION_MAPPING;
    const sectionName = SECTION_MAPPING[sectionId];
    return {
      ...memo,
      [sectionName]: {
        ...(memo[sectionName] ?? {}),
        [key]: data.data[key],
      },
    };
  }, {} as FullDocument);

  if (data.meta.name) {
    serializedDocument.name = valueResolver(data.meta.name);
  }

  if (data.meta.type) {
    serializedDocument.type = valueResolver(data.meta.type);
  }

  for (const sectionName of Object.values(SECTION_MAPPING)) {
    serializedDocument[sectionName] = serializedDocument[sectionName] ?? null;
  }

  return serializedDocument;
};

const validateValue = (value: DocumentValue | DocumentValue[], resolver: ValueResolver): boolean => {
  try {
    // Attempt to resolve the value, it will throw an error if the value is not valid
    resolver(value);
    return true;
  } catch (error) {
    logger.error('Error validating value:', error);
    return false;
  }
};

export const deserializeDocument = (document: FullDocument, resolver: ValueResolver): DocumentData => {
  const data = Object.values(SECTION_MAPPING).reduce(
    (memo, sectionName) => {
      if (!document[sectionName]) return memo;

      const filteredFields = Object.entries(document[sectionName]).reduce(
        (fieldsMemo, [key, value]) => {
          if (
            // Filter null fields returned by the API
            (Array.isArray(value) || value !== null) &&
            // Ensure every reference is valid, otherwise omit
            validateValue(value, resolver)
          ) {
            fieldsMemo[key] = value;
          }

          return fieldsMemo;
        },
        {} as DocumentData['data'],
      );

      return {...memo, ...filteredFields};
    },
    {} as DocumentData['data'],
  );

  const newDocument: DocumentData = {
    data,
    meta: {
      id: document.id,
      name: document.name,
      type: document.type,
      updatedAt: document.updatedAt,
    },
  };

  return newDocument;
};

export const mapAttachmentIdFromFieldId = (fieldId: string): DocumentAttachmentId => {
  switch (fieldId) {
    case InternalDocumentAttachmentId.T_specimen:
    case InternalDocumentAttachmentId.N_specimen:
    case InternalDocumentAttachmentId.O_specimen:
    case InternalDocumentAttachmentId.F_specimen:
      return DocumentAttachmentId.AA2;
  }

  return fieldId as DocumentAttachmentId;
};

const serializeFieldId = (fieldId: string): string => {
  // Some fields require a different fieldId when serializing them
  if (fieldId in InternalDocumentAttachmentId) {
    return mapAttachmentIdFromFieldId(fieldId);
  }
  if (ALLOCATION_METHOD_FIELDS.some(field => field === fieldId)) {
    return ALLOCATION_METHOD_FIELD;
  }
  return fieldId;
};

const serializeArrayFieldId = (sectionId: string, fieldId: string): string => {
  if (sectionId == SECTION_I) {
    // Remove the suffix for array fields in the section I
    if (fieldId.match(/^I.*_[012]$/)) {
      return fieldId.replace(/_[012]$/, '');
    }
  }
  return fieldId;
};

const resolveValueIfReference = (
  resolvedValueOrRef: Reference | string | boolean | null,
  valueResolver: ValueResolver,
  visibilitySet: VisibilitySet,
): string | boolean | null => {
  if (!!resolvedValueOrRef && typeof resolvedValueOrRef === 'object' && '$ref' in resolvedValueOrRef) {
    // When value is a ref with property we resolve it to get the actual value
    if ('property' in resolvedValueOrRef) {
      return valueResolver(resolvedValueOrRef, {visibilitySet});
    }

    if (resolvedValueOrRef.$ref.startsWith(DOMAIN_VALUE_BOOLEAN)) {
      // When value is a DV-BOOLEAN ref we just serialize the value casted as a boolean
      return valueResolver(resolvedValueOrRef, {visibilitySet}) === 'true';
    }

    // When value is a ref (and not a DV-BOOLEAN ref) we just serialize the reference (e.g. 'DV-3#1')
    return resolvedValueOrRef.$ref;
  }

  return resolvedValueOrRef;
};

const serializeValue = (
  fieldName: string,
  value: DocumentValue | DocumentValue[],
  valueResolver: ValueResolver,
  visibilitySet: Set<string>,
  arrayKeyName?: string,
): string | boolean | (string | boolean | null)[] | null => {
  if (Array.isArray(value)) {
    return value.flatMap(item => serializeValue(fieldName, item, valueResolver, visibilitySet, arrayKeyName));
  }

  const resolvedValueOrRef = valueResolver<Reference | string | boolean | null>(value, {
    visibilitySet,
    keepDomainReference: true,
  });

  // The order of the transformations is important
  let resolvedValue = resolveValueIfReference(resolvedValueOrRef, valueResolver, visibilitySet);

  // When value is one of theses fields: R11, R8, references are converted to their description
  if (['R11', 'R8'].includes(fieldName)) {
    resolvedValue = valueResolver(value, {visibilitySet});
  }

  // When resolvedValue is DV-32#other, we serialize it as 999
  if (resolvedValue === 'DV-32#other') {
    resolvedValue = '999';
  }

  // When value is in one of these arrays: J1_0, J1_2, W_0, W_2 or one of theses fields: I138_2, O31
  // The reference is converted to only the choice index. (e.g. 'DV-3#1' become '1')
  if (['J1_0', 'J1_2', 'W_0', 'W_2'].includes(arrayKeyName ?? '') || ['I138_2', 'O31'].includes(fieldName)) {
    resolvedValue =
      typeof value === 'object' &&
      '$ref' in value &&
      value.$ref.startsWith(DOMAIN_VALUE_PREFIX) &&
      String(resolvedValue).includes('#')
        ? String(resolvedValue).split('#')[1]
        : resolvedValue;
  }

  // Other transformations based on the field name
  const transformedValue = transformByFieldName(fieldName)(resolvedValue);

  // When the transformed value is an empty string, we return null except for some fields
  if (['W9', 'J18'].includes(fieldName)) {
    return transformedValue;
  }
  return transformedValue === '' ? null : transformedValue;
};

const subsectionsFromDepositType = (
  depositType: string | null,
  sectionName: keyof FullDocumentReadyForSignature,
  visibilitySet: VisibilitySet,
): (keyof FullDocumentReadyForSignature)[] => {
  switch (depositType) {
    case null:
      return [sectionName];
    case 'eft':
      return [SECTION_J_EFT];
    case 'cash':
      return [SECTION_J_CASH];
    case 'periodic':
      return [SECTION_J_PERIODIC];
    case 'transfer':
      return [SECTION_J_TRANSFER];
    default:
      return [SECTION_J_EFT, SECTION_J_CASH, SECTION_J_PERIODIC, SECTION_J_TRANSFER].flatMap(section => {
        return visibilitySet.has(section) ? [section as keyof FullDocumentReadyForSignature] : [];
      });
  }
};

const keyIndexFromSectionId = (sectionId: string) => {
  switch (sectionId) {
    case SECTION_I:
      return '1';
    case SECTION_J_ALL:
      return '1';
    case SECTION_O:
      return '1';
    case SECTION_R:
      return '2';
    case SECTION_W:
    default:
      return '';
  }
};

const getArraySerializer = (arrayDataType: ARRAY_DATA_TYPES) => {
  switch (arrayDataType) {
    case INVESTMENT_ACCOUNT_DATA_TYPES.CIQ:
    case INVESTMENT_ACCOUNT_DATA_TYPES.CIG:
    case INVESTMENT_ACCOUNT_DATA_TYPES.CI:
    case INVESTMENT_ACCOUNT_DATA_TYPES.CHR:
    case INVESTMENT_ACCOUNT_DATA_TYPES.FPG:
      return serializeInvestmentAccountArray;
    case BENEFICIARIES_ARRAY_TYPES.BEN:
    case BENEFICIARIES_ARRAY_TYPES.COBEN:
    default:
      return serializeAsSingleLevelArray;
  }
};

const accountTypeIndexFromAccountType = (sectionId: string, accountType: INVESTMENT_ACCOUNT_DATA_TYPES) => {
  switch (accountType) {
    case INVESTMENT_ACCOUNT_DATA_TYPES.CIQ:
      return 0;
    case INVESTMENT_ACCOUNT_DATA_TYPES.CIG:
      return sectionId === SECTION_I ? 2 : 1;
    case INVESTMENT_ACCOUNT_DATA_TYPES.CI:
      return sectionId === SECTION_I ? 1 : 2;
    case INVESTMENT_ACCOUNT_DATA_TYPES.CHR:
      return 3;
    case INVESTMENT_ACCOUNT_DATA_TYPES.FPG:
      return 4;
  }
};

type ArraySerializerArguments = {
  memo: FullDocumentReadyForSignature;
  key: string;
  value: DocumentValue | DocumentValue[];
  keyPrefix: string;
  arrayDataType: INVESTMENT_ACCOUNT_DATA_TYPES;
  entryIndex: string;
  data: DocumentData;
  visibilitySet: Set<string>;
  valueResolver: ValueResolver;
};

const serializeInvestmentAccountArray = (args: ArraySerializerArguments) => {
  const {
    memo,
    value,
    keyPrefix,
    arrayDataType: accountType,
    entryIndex: accountIndex,
    data,
    visibilitySet,
    valueResolver,
  } = args;
  const accountTypeIndex = accountTypeIndexFromAccountType(keyPrefix, accountType);
  if (accountTypeIndex == null) return memo;

  const relevantFields = valueResolver(value).split(',');
  const depositTypeField = relevantFields.find(field => field.match(new RegExp(`DepositType#${accountIndex}$`)));
  const depositType = depositTypeField ? valueResolver(data.data[depositTypeField]) : null;
  const subsections = subsectionsFromDepositType(
    depositType,
    keyPrefix as keyof FullDocumentReadyForSignature,
    visibilitySet,
  );

  for (const subsection of subsections) {
    const newKey = `${keyPrefix}${keyIndexFromSectionId(keyPrefix)}_${accountTypeIndex}`;

    const newArrayItem = relevantFields.reduce((memo, field) => {
      const fieldName = field.replace(/#\d+/, '');
      if (fieldName.startsWith(`${keyPrefix}_`)) return memo;
      const value = valueResolver<string | Reference>(
        {$ref: field},
        {keepDomainReference: true, visibilitySet: visibilitySet},
      );

      return {
        ...memo,
        [serializeArrayFieldId(keyPrefix, fieldName)]: serializeValue(
          fieldName,
          value,
          valueResolver,
          visibilitySet,
          newKey,
        ),
      };
    }, {});

    const sectionId = subsection as keyof FullDocumentReadyForSignature;
    const section = memo[sectionId] ?? {};
    const existingArrayItem = (section[newKey] as [] | undefined) ?? [];

    memo[sectionId] = {
      ...memo[sectionId],
      [newKey]: [...existingArrayItem, newArrayItem],
    };
  }

  return memo;
};

const serializeAsSingleLevelArray = (args: ArraySerializerArguments) => {
  const {memo, value, keyPrefix, visibilitySet, valueResolver} = args;

  const relevantFields = valueResolver(value).split(',');

  const sectionId = keyPrefix as keyof FullDocumentReadyForSignature;

  const arrayItem = relevantFields.reduce((memo, field) => {
    const fieldName = field.replace('#', '-');
    const value = valueResolver<string | Reference>(
      {$ref: field},
      {keepDomainReference: true, visibilitySet: visibilitySet},
    );

    return {
      ...memo,
      [serializeArrayFieldId(sectionId, fieldName)]: serializeValue(fieldName, value, valueResolver, visibilitySet),
    };
  }, {});

  memo[sectionId] = {
    ...memo[sectionId],
    ...arrayItem,
  };

  return memo;
};

const ignoredSuffixes = ['[open]', '[count]', '[new]', '[draft]'];

export const serializeDocumentForSignature = (
  data: DocumentData,
  model: Model,
  visibilitySet: Set<string>,
  valueResolver: ValueResolver,
): FullDocumentReadyForSignature => {
  const serializedDocument = Object.entries(data.data).reduce((memo, [key, value]) => {
    if (key.match(/^_[A-Z]_/)) {
      // If the key is a private property for the engine
      if (ignoredSuffixes.some(suffix => key.endsWith(suffix))) return memo;

      const matches = key.match(/^_([A-Z])_(.+)#(\d)+\[fields\]$/i);

      if (matches) {
        // If the key matches this format: "_X_arrayDataType#index[fields]"
        // We can use it to serialize the array entry
        const keyPrefix = matches[1];
        const arrayDataType = matches[2];
        if (arrayDataType == null) return memo;
        const entryIndex = matches[3];

        const arraySerializer = getArraySerializer(arrayDataType as ARRAY_DATA_TYPES);

        const args = {
          memo,
          key,
          value,
          keyPrefix,
          arrayDataType: arrayDataType as INVESTMENT_ACCOUNT_DATA_TYPES,
          entryIndex,
          data,
          visibilitySet,
          valueResolver,
        };

        memo = arraySerializer(args);
      }

      return memo;
    }

    const accessRecord = model.accessMap.get(key);
    const sectionIndex = accessRecord && 'sectionIndex' in accessRecord ? accessRecord.sectionIndex : null;
    const sections = model.form.sections;
    const section = sectionIndex !== null ? sections[sectionIndex] : null;

    if (!visibilitySet.has(key) || key.includes('#') || !section || section.id.endsWith('+')) {
      return memo;
    }

    return {
      ...memo,
      [section.id]: {
        ...memo[section.id as keyof FullDocumentReadyForSignature],
        [serializeFieldId(key)]: serializeValue(key, value, valueResolver, visibilitySet),
      },
    };
  }, {} as FullDocumentReadyForSignature);

  return serializedDocument;
};
